import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { content } from '../data/content';
import { PACKS, PRODUCT, ZONES, defaultZone, formatPrice, packTotal } from '../data/product';
import { saveOrder, warmFirebase } from '../lib/orders';
import { trackInitiateCheckout, trackPurchase } from '../lib/pixel';
import { Eyebrow } from './ui/Ui';

const initialForm = { name: '', phone: '', address: '' };

/** أرقام الجوال المحلية: 9-10 خانات، مع أو بدون مفتاح دولي */
const PHONE_RE = /^(\+?\d{1,4})?\d{9,10}$/;

const TRUST = [
  { icon: 'fa-hand-holding-dollar', color: 'text-z-green', label: content.trustCod },
  { icon: 'fa-truck-fast', color: 'text-z-orange', label: content.trustDelivery },
  { icon: 'fa-shield', color: 'text-z-green', label: content.trustWarranty },
];

/** خطوات ما بعد الطلب — يعرفها الزبون فيرتاح بدل أن ينتظر مجهولاً */
const NEXT_STEPS = [
  { icon: 'fa-phone-volume', title: 'بنتصل فيك', desc: 'خلال ساعة لتأكيد العنوان ووقت التوصيل' },
  { icon: 'fa-box-open', title: 'بنجهّز ونشحن', desc: 'الطلب بيطلع من مستودعنا بنفس اليوم' },
  { icon: 'fa-hand-holding-dollar', title: 'بتستلم وبتدفع', desc: 'الدفع كاش عند المندوب — ولا شيكل قبل' },
];

/**
 * شاشة تأكيد الطلب.
 *
 * ثلاثة أشياء تصنع الطمأنينة هنا: رقم مرجعي يثبت أن الطلب صار له وجود، ملخّص
 * يطابق ما اختاره فيتأكد أننا فهمناه، وخطوات قادمة تُنهي سؤال «وبعدين؟». بلا
 * هذه الثلاثة تبقى الصفحة مجرّد كلمة «تم» لا تُطمئن أحداً.
 */
function OrderConfirmed({ orderId, name, pack, zone, total, whatsappUrl }) {
  /**
   * الصعود لأعلى الشاشة عند الظهور.
   *
   * لا يحدث تنقّل بين مسارين هنا — الصفحة نفسها تستبدل النموذج بشاشة التأكيد،
   * فيبقى موضع التمرير حيث كان الزبون: عند زر التأكيد في آخر النموذج. فتُفتح
   * شاشة التأكيد من منتصفها ولا يرى العنوان ولا رقم الطلب.
   */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
  }, []);

  // أول ستة محارف تكفي كمرجع بشري يُقرأ في مكالمة، والمعرّف الكامل في اللوحة
  const reference = orderId ? orderId.slice(0, 6).toUpperCase() : '——————';
  const firstName = String(name || '').trim().split(/\s+/)[0];

  return (
    <section className="container mx-auto max-w-2xl py-10 md:py-16">
      <div className="text-center">
        {/* حلقتان متمدّدتان خلف العلامة تعطيان إحساس «تمّ» متحرّكاً بلا مكتبة */}
        <div className="relative w-24 h-24 mx-auto mb-6">
          <span className="absolute inset-0 rounded-full bg-z-green/10 animate-ping" />
          <span className="absolute inset-2 rounded-full bg-z-green/10" />
          <span className="absolute inset-0 flex items-center justify-center text-4xl text-z-green">
            <i className="fa-solid fa-circle-check" />
          </span>
        </div>

        <h1 className="text-3xl md:text-5xl font-serif font-bold text-z-navy mb-3 tracking-tight">
          تم اعتماد <span className="text-z-green">طلبك</span>
        </h1>

        <p className="text-sm md:text-base text-z-gray max-w-md mx-auto leading-relaxed">
          {firstName ? `شكراً ${firstName}، ` : ''}استلمنا طلبك وسجّلناه. بنتواصل معك خلال ساعة لتأكيد التفاصيل.
        </p>

        {/* الرقم المرجعي بأرقام لاتينية واتجاه LTR — يُقرأ حرفاً حرفاً في المكالمة */}
        <div className="inline-flex items-center gap-3 mt-6 bg-z-light rounded-full ps-5 pe-3 py-2">
          <span className="text-xs text-z-gray">رقم الطلب</span>
          <span className="font-serif font-bold text-lg text-z-navy tracking-widest" dir="ltr">
            {reference}
          </span>
        </div>
      </div>

      {/* ===== ملخّص ما تم اعتماده ===== */}
      <div className="mt-8 bg-white border border-gray-200 rounded-3xl overflow-hidden">
        <div className="px-5 md:px-6 py-4 border-b border-gray-100 flex items-center gap-3">
          <i className="fa-solid fa-receipt text-z-orange" />
          <h2 className="font-bold text-z-navy">ملخّص الطلب</h2>
        </div>

        <dl className="px-5 md:px-6 py-4 space-y-3 text-sm">
          <SummaryRow label="المنتج">{content.orderProduct}</SummaryRow>
          <SummaryRow label="الكمية">{pack.name}</SummaryRow>
          <SummaryRow label="التوصيل">
            {zone.name} — {zone.fee === 0 ? content.orderFree : formatPrice(zone.fee)}
          </SummaryRow>
          <SummaryRow label="طريقة الدفع">{content.trustCod}</SummaryRow>
        </dl>

        <div className="px-5 md:px-6 py-4 bg-z-light flex items-center justify-between">
          <span className="text-sm font-bold text-z-navy">المبلغ المستحق عند الاستلام</span>
          <span className="text-2xl font-bold text-z-navy">{formatPrice(total)}</span>
        </div>
      </div>

      {/* ===== الخطوات القادمة ===== */}
      <ol className="mt-8 grid sm:grid-cols-3 gap-4">
        {NEXT_STEPS.map((step, i) => (
          <li key={step.title} className="relative bg-z-light rounded-2xl p-4 md:p-5">
            <span className="absolute top-4 end-4 font-serif font-bold text-2xl text-z-navy/10">{i + 1}</span>
            <span className="w-10 h-10 rounded-xl bg-white text-z-navy flex items-center justify-center mb-3">
              <i className={`fa-solid ${step.icon}`} />
            </span>
            <h3 className="font-bold text-z-navy text-sm mb-1">{step.title}</h3>
            <p className="text-xs text-z-gray leading-relaxed">{step.desc}</p>
          </li>
        ))}
      </ol>

      {/* ===== إجراءات اختيارية ===== */}
      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Link
          to="/"
          className="w-full sm:w-auto bg-z-navy text-white px-8 py-3.5 font-bold rounded-full transition hover:bg-z-navy/90 flex items-center justify-center gap-2"
        >
          <i className="fa-solid fa-house" /> رجوع للمتجر
        </Link>

        {/* الواتساب صار اختياراً لا إجباراً: الطلب وصل، وهذا للاستفسار فقط */}
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full sm:w-auto border-2 border-z-green text-z-green px-8 py-3 font-bold rounded-full transition hover:bg-z-green hover:text-white flex items-center justify-center gap-2"
        >
          <i className="fa-brands fa-whatsapp text-lg" /> استفسار عن الطلب
        </a>
      </div>

      <p className="text-center text-xs text-z-gray mt-6">
        {content.orderOrCall}:{' '}
        <a href={`tel:${PRODUCT.phone.replace(/\s/g, '')}`} className="text-z-navy font-bold" dir="ltr">
          {PRODUCT.phone}
        </a>
      </p>
    </section>
  );
}

function SummaryRow({ label, children }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-z-gray shrink-0">{label}</dt>
      <dd className="text-z-navy font-semibold text-end">{children}</dd>
    </div>
  );
}

/**
 * نموذج الطلب: بلا سلة وبلا صفحة دفع وبلا تسجيل حساب — خطوة واحدة.
 * على الجوال يظهر الإجمالي في شريط ثابت أسفل الشاشة حتى يبقى واضحاً
 * أثناء تعبئة الحقول، لأن ملخّص الطلب ينزل تحت النموذج في العمود الواحد.
 *
 * ثلاثة حقول كتابة فقط: الاسم والجوال والعنوان. الكمية والمنطقة اختيار بنقرة،
 * وحقل الملاحظات حُذف — كل حقل إضافي في نموذج الطلب يُسقط نسبة من المكملين،
 * والحقل الاختياري يدفع العميل للتوقّف والتفكير فيما «يجب» أن يكتبه.
 */
export default function OrderForm({ pack, setPack }) {
  const [form, setForm] = useState(initialForm);
  const [zone, setZone] = useState(defaultZone);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [orderId, setOrderId] = useState('');

  /**
   * قفل الإرسال. حالة sending لا تصلح للحماية: setState لا يغيّر القيمة فوراً
   * بل عند الرسمة التالية، فضغطتان في نفس جزء الثانية تمرّان معاً من الشرط
   * فيُنشأ طلبان ويُطلق حدث شراء مرتين. الـ ref يتغيّر في نفس اللحظة.
   */
  const submitting = useRef(false);

  const total = packTotal(pack) + zone.fee;

  // يجلب حزمة Firebase ويفتح الجلسة أثناء تعبئة الحقول، فلا ينتظرها الزبون
  // لحظة الضغط على «تأكيد الطلب»
  useEffect(() => {
    warmFirebase();
  }, []);

  // InitiateCheckout مرة واحدة عند فتح الصفحة لا مع كل تغيير كمية أو منطقة —
  // مصفوفة اعتماديات فارغة رغم استعمال pack و zone، وهو المقصود
  useEffect(() => {
    trackInitiateCheckout({ value: packTotal(pack) + zone.fee, quantity: pack.qty });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 3) next.name = content.orderErrName;
    if (!PHONE_RE.test(form.phone.replace(/[\s-]/g, ''))) next.phone = content.orderErrPhone;
    if (form.address.trim().length < 8) next.address = content.orderErrAddress;
    setErrors(next);

    // ينقل المستخدم لأول حقل فيه خطأ بدل أن يبحث عنه بنفسه
    const firstError = Object.keys(next)[0];
    if (firstError) document.getElementById(firstError)?.focus();

    return Object.keys(next).length === 0;
  };

  const whatsappUrl = useMemo(() => {
    const lines = [
      '🛒 طلب جديد من موقع zalyas',
      `المنتج: ${content.orderProduct}`,
      `الكمية: ${pack.qty}`,
      `المنطقة: ${zone.name} — التوصيل ${zone.fee === 0 ? content.orderFree : formatPrice(zone.fee)}`,
      `الإجمالي: ${formatPrice(total)} (الدفع عند الاستلام)`,
      '—',
      `الاسم: ${form.name}`,
      `الجوال: ${form.phone}`,
      `العنوان: ${form.address}`,
    ];

    return `https://wa.me/${PRODUCT.whatsapp}?text=${encodeURIComponent(lines.join('\n'))}`;
  }, [form, pack, zone, total]);

  /**
   * الحفظ في قاعدة البيانات هو الطلب نفسه.
   *
   * لم تعد محادثة الواتساب تُفتح تلقائياً — كانت تقاطع الزبون بنافذة إذن من
   * النظام في أحرج لحظة، وتترك مصير الطلب معلّقاً على ضغطه «إرسال» في تطبيق
   * آخر. لذلك لا يُعلَن النجاح إلا إذا وصل الطلب فعلاً؛ وإن فشل الحفظ نقول ذلك
   * صراحةً ونعطيه الواتساب كمخرج يدوي بدل أن نطمئنه على طلب لم يصل.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting.current || !validate()) return;

    submitting.current = true;
    setSending(true);
    setFailed(false);

    try {
      const id = await saveOrder({ form, pack, zone });

      // Purchase بعد نجاح الحفظ حصراً: إطلاقه عند الفشل يضخّم تحويلات الحملة
      // بطلبات لا وجود لها
      trackPurchase({ value: total, quantity: pack.qty });

      setOrderId(id);
      setSubmitted(true);
    } catch (error) {
      console.error('تعذّر حفظ الطلب في قاعدة البيانات', error);
      setFailed(true);
      // يُفتح القفل عند الفشل وحده — بعد النجاح تُعرض شاشة التأكيد ولا سبيل
      // لإرسال ثانٍ، وفتحه هناك يفتح باباً لطلب مكرّر
      submitting.current = false;
    } finally {
      setSending(false);
    }
  };

  const inputClass = (field) =>
    // النص النائب بلون كامل لا مخفّف: عند 60% تنزل نسبة التباين إلى 2.5:1
    // وهي دون الحد المطلوب، وهذا أكثر ما يخصم من درجة إمكانية الوصول
    `w-full bg-white border rounded-2xl px-4 md:px-5 py-3.5 text-sm outline-none transition text-z-navy placeholder-z-gray ${
      errors[field] ? 'border-z-red' : 'border-gray-200 focus:border-z-navy'
    }`;

  if (submitted) {
    return (
      <OrderConfirmed
        orderId={orderId}
        name={form.name}
        pack={pack}
        zone={zone}
        total={total}
        whatsappUrl={whatsappUrl}
      />
    );
  }

  return (
    <section className="container mx-auto max-w-5xl py-8 md:py-16 pb-28 lg:pb-16">
      <div className="text-center mb-8 md:mb-12">
        <Eyebrow icon="fa-truck-fast" iconColor="text-z-orange" label={content.orderEyebrow} />
        <h1 className="text-2xl md:text-4xl font-bold font-serif text-z-navy mb-3">{content.orderTitle}</h1>
        <p className="text-sm md:text-base text-z-gray max-w-xl mx-auto">{content.orderSubtitle}</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* ===== النموذج ===== */}
        <form onSubmit={handleSubmit} noValidate className="lg:col-span-3 space-y-5">
          {/* الكمية أولاً: يختار ثم يعبّي، لا العكس */}
          <div>
            <span className="block text-sm font-bold text-z-navy mb-2">{content.orderQty}</span>
            <div className="grid grid-cols-3 gap-2 md:gap-3">
              {PACKS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setPack(item)}
                  className={`relative rounded-2xl border-2 py-3 px-1 text-center transition ${
                    pack.id === item.id ? 'border-z-red bg-z-red/5' : 'border-gray-200 bg-white hover:border-z-red/40'
                  }`}
                >
                  {item.tag && (
                    <span className="absolute -top-2.5 inset-x-0 mx-auto w-max bg-z-red text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      {item.tag}
                    </span>
                  )}
                  <span className="block font-bold text-sm text-z-navy mt-1">{item.name}</span>
                  <span className="block text-xs text-z-gray mt-1">{formatPrice(packTotal(item))}</span>
                </button>
              ))}
            </div>
          </div>

          {/* المنطقة بعد الكمية مباشرة: الاثنان يحدّدان المبلغ، فيرى إجماليه
              كاملاً قبل أن يكتب حرفاً واحداً — لا مفاجأة أجرة في آخر خطوة */}
          <div>
            <span className="block text-sm font-bold text-z-navy mb-2">{content.orderZone}</span>
            <div className="grid grid-cols-3 gap-2 md:gap-3">
              {ZONES.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setZone(item)}
                  className={`rounded-2xl border-2 py-3 px-1 text-center transition ${
                    zone.id === item.id ? 'border-z-red bg-z-red/5' : 'border-gray-200 bg-white hover:border-z-red/40'
                  }`}
                >
                  <span className="block font-bold text-sm text-z-navy">{item.name}</span>
                  <span
                    className={`block text-xs mt-1 font-bold ${item.fee === 0 ? 'text-z-green' : 'text-z-gray'}`}
                  >
                    {item.fee === 0 ? content.orderFree : formatPrice(item.fee)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="name" className="block text-sm font-bold text-z-navy mb-2">
                {content.orderName}
              </label>
              <input
                id="name"
                type="text"
                autoComplete="name"
                value={form.name}
                onChange={update('name')}
                placeholder={content.orderNamePlaceholder}
                className={inputClass('name')}
              />
              {errors.name && <p className="text-xs text-z-red mt-2">{errors.name}</p>}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-bold text-z-navy mb-2">
                {content.orderPhone}
              </label>
              <input
                id="phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                dir="ltr"
                value={form.phone}
                onChange={update('phone')}
                placeholder={content.orderPhonePlaceholder}
                className={`${inputClass('phone')} text-start`}
              />
              {errors.phone && <p className="text-xs text-z-red mt-2">{errors.phone}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="address" className="block text-sm font-bold text-z-navy mb-2">
              {content.orderAddress}
            </label>
            <input
              id="address"
              type="text"
              autoComplete="street-address"
              value={form.address}
              onChange={update('address')}
              placeholder={content.orderAddressPlaceholder}
              className={inputClass('address')}
            />
            {errors.address && <p className="text-xs text-z-red mt-2">{errors.address}</p>}
          </div>

          {/* زر الإرسال ظاهر داخل النموذج على الشاشات الكبيرة فقط —
              على الجوال يقوم مقامه الشريط الثابت أسفل الشاشة */}
          <button
            type="submit"
            disabled={sending}
            className="hidden lg:flex w-full bg-z-red text-white px-10 py-4 font-bold text-lg hover:bg-z-red/90 rounded-full transition-all items-center justify-center gap-3 shadow-lg shadow-z-red/30 disabled:opacity-70"
          >
            {sending ? (
              <>
                <i className="fa-solid fa-spinner fa-spin text-xl" /> {content.orderSending}
              </>
            ) : (
              <>
                <i className="fa-solid fa-circle-check text-xl" /> {content.orderSubmit}
              </>
            )}
          </button>

          {/* لم تعد محادثة الواتساب تُفتح تلقائياً، فالحفظ الفاشل يعني طلباً
              ضائعاً — نقولها صراحةً ونعطيه مخرجاً يدوياً بدل رسالة نجاح كاذبة */}
          {failed && (
            <div className="dash-card bg-white p-4 md:p-5 text-center">
              <i className="fa-solid fa-triangle-exclamation text-z-red text-xl mb-2 block" />
              <p className="text-sm font-bold text-z-navy mb-1">ما قدرنا نسجّل الطلب</p>
              <p className="text-xs text-z-gray leading-relaxed mb-4">
                يمكن الإنترنت انقطع. جرّب «تأكيد الطلب» مرة ثانية، أو ابعتلنا الطلب على واتساب مباشرة.
              </p>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-z-green text-white px-6 py-3 font-bold text-sm rounded-full transition hover:bg-z-green/90"
              >
                <i className="fa-brands fa-whatsapp text-lg" /> إرسال الطلب على واتساب
              </a>
            </div>
          )}

          <p className="text-xs text-z-gray text-center flex items-center justify-center gap-2">
            <i className="fa-solid fa-lock text-z-green" /> {content.orderMicro}
          </p>

          {/* ===== شريط الإجمالي والإرسال الثابت — الجوال فقط ===== */}
          <div className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-white border-t border-gray-200 shadow-md px-4 py-3 flex items-center gap-3">
            <div className="shrink-0">
              <p className="text-[10px] text-z-gray leading-none mb-1">{content.orderTotalShort}</p>
              <p className="text-xl font-bold text-z-navy leading-none">{formatPrice(total)}</p>
            </div>
            <button
              type="submit"
              disabled={sending}
              className="flex-1 bg-z-red text-white py-3.5 font-bold rounded-full transition-all flex items-center justify-center gap-2 shadow-lg shadow-z-red/30 disabled:opacity-70"
            >
              {sending ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" /> {content.orderSending}
                </>
              ) : (
                <>
                  <i className="fa-solid fa-circle-check" /> {content.orderSubmitShort}
                </>
              )}
            </button>
          </div>
        </form>

        {/* ===== ملخص الطلب ===== */}
        <aside className="lg:col-span-2">
          <div className="bg-z-light rounded-3xl p-6 md:p-7 border border-gray-200 lg:sticky lg:top-24">
            <h2 className="text-lg font-bold text-z-navy mb-5 pb-4 border-b border-gray-200 flex items-center gap-3">
              <i className="fa-solid fa-receipt text-z-orange" /> {content.orderSummary}
            </h2>

            <div className="flex gap-4 mb-5">
              <div className="w-20 h-20 rounded-2xl bg-white border border-gray-200 shrink-0 overflow-hidden">
                <img src="/p3.jpg" alt={content.orderProduct} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-z-navy leading-snug">{content.orderProduct}</p>
                <p className="text-xs text-z-gray mt-2">
                  {pack.name} · {formatPrice(pack.unitPrice)} {content.offerPerUnit}
                </p>
              </div>
            </div>

            <dl className="space-y-3 text-sm border-t border-gray-200 pt-5">
              <div className="flex justify-between text-z-gray">
                <dt>{content.orderSubtotal}</dt>
                <dd className="text-z-navy font-bold">{formatPrice(packTotal(pack))}</dd>
              </div>
              <div className="flex justify-between text-z-gray">
                <dt>
                  {content.orderShipping} — {zone.name}
                </dt>
                <dd className={`font-bold ${zone.fee === 0 ? 'text-z-green' : 'text-z-navy'}`}>
                  {zone.fee === 0 ? content.orderFree : formatPrice(zone.fee)}
                </dd>
              </div>
              <div className="flex justify-between items-end pt-4 border-t border-gray-200">
                <dt className="text-z-navy font-bold text-xs max-w-[55%] leading-snug">{content.orderTotal}</dt>
                <dd className="text-3xl font-bold text-z-navy">{formatPrice(total)}</dd>
              </div>
            </dl>

            {/* تذكير أخير بالضمانات في اللحظة الفاصلة */}
            <ul className="mt-6 pt-5 border-t border-gray-200 grid grid-cols-2 gap-3 text-xs text-z-gray">
              {TRUST.map((item) => (
                <li key={item.label} className="flex items-center gap-2">
                  <i className={`fa-solid ${item.icon} ${item.color}`} /> {item.label}
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </section>
  );
}
